"use client"

import { FADE_MASK_STYLE } from "@/utils/masonryMask"

interface DataPoint {
  label: string
  x: number
  y: number
  type: "stablecoin" | "tokenized"
  highlighted?: boolean
}

const dataPoints: DataPoint[] = [
  { label: "EURC (Base)", x: 20, y: 85, type: "stablecoin" },
  { label: "USDC (HyperEVM)", x: 88, y: 48, type: "stablecoin", highlighted: true },
  { label: "EURC (Avalanche)", x: 25, y: 65, type: "stablecoin" },
  { label: "USDC (Cardano)", x: 30, y: 62, type: "stablecoin" },
  { label: "USDC (XDC)", x: 35, y: 55, type: "stablecoin" },
  { label: "USYC (Solana)", x: 12, y: 48, type: "tokenized" },
  { label: "USDC (Ink)", x: 15, y: 44, type: "stablecoin" },
  { label: "EURC (Stellar)", x: 18, y: 38, type: "stablecoin" },
  { label: "USDC (Solana)", x: 42, y: 46, type: "stablecoin" },
  { label: "USDC (Starknet)", x: 38, y: 47, type: "stablecoin" },
  { label: "USDC (OP Mainnet)", x: 44, y: 45, type: "stablecoin" },
  { label: "USDC (Base)", x: 46, y: 44, type: "stablecoin" },
  { label: "USDC (Ethereum)", x: 40, y: 42, type: "stablecoin" },
  { label: "USDC (zkSync)", x: 36, y: 37, type: "stablecoin" },
  { label: "USDC (Avalanche)", x: 34, y: 36, type: "stablecoin" },
  { label: "USDT (BNB Chain)", x: 32, y: 34, type: "stablecoin" },
  { label: "BUIDL", x: 48, y: 43, type: "tokenized" },
  { label: "USDC (Polygon)", x: 43, y: 40, type: "stablecoin" },
  { label: "Polkadot AssetHub", x: 30, y: 32, type: "tokenized" },
]

function Dot({
  point,
  chartWidth,
  chartHeight,
}: {
  point: DataPoint
  chartWidth: number
  chartHeight: number
}) {
  const cx = (point.x / 100) * chartWidth
  const cy = chartHeight - (point.y / 100) * chartHeight
  const r = point.highlighted ? 7 : 4.5
  const fill = point.type === "stablecoin" ? "rgb(16, 185, 129)" : "rgb(139, 92, 246)"

  return (
    <g>
      {point.highlighted && (
        <rect
          x={cx - 42}
          y={cy - 26}
          width={84}
          height={52}
          rx={18}
          fill="none"
          stroke="rgb(16, 185, 129)"
          strokeWidth="1.5"
          strokeDasharray="4 3"
          className="opacity-0 group-hover/scatter:opacity-60 transition-opacity duration-300"
        />
      )}
      <circle cx={cx} cy={cy} r={r + 2} fill={fill} opacity={0.15} />
      <circle cx={cx} cy={cy} r={r} fill={fill} opacity={0.9} />
      <circle cx={cx} cy={cy} r={r - 2} className="fill-white dark:fill-white" opacity={0.3} />
      {!point.highlighted && (
        <text x={cx + r + 4} y={cy + 3} className="fill-fg-secondary" fontSize={6.5}>
          {point.label}
        </text>
      )}
    </g>
  )
}

export function ScatterPlotDemo() {
  const chartWidth = 520
  const chartHeight = 300
  const padding = { top: 10, right: 20, bottom: 30, left: 50 }

  const yLabels = ["+150%", "+100%", "+50%", "0%", "-50%", "-100%"]
  const xLabels = ["-100%", "-50%", "0%", "+50%", "+100%", "+150%", "+200%"]

  return (
    <div
      className="relative border-t border-l border-solid border-border-default rounded-tl-xl bg-bg-default"
      style={{
        maxHeight: "400px",
        overflow: "hidden",
        ...FADE_MASK_STYLE,
      }}
    >
      <div className="p-5">
        {/* Title */}
        <div className="mb-1">
          <h4 className="text-sm font-semibold text-fg-default leading-snug">
            The market cap of USDC on HyperEVM is up ~140% over the past month
          </h4>
          <p className="text-[11px] text-fg-secondary mt-0.5">
            HyperEVM has seen ~$1B in USDC inflows since the start of the year
          </p>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mb-3">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
            <span className="text-[9px] text-fg-secondary">Stablecoins</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-violet-500" />
            <span className="text-[9px] text-fg-secondary">Tokenized funds</span>
          </div>
        </div>

        {/* Chart */}
        <div className="relative">
          <svg
            viewBox={`0 0 ${chartWidth + padding.left + padding.right} ${chartHeight + padding.top + padding.bottom}`}
            className="w-full"
          >
            <g transform={`translate(${padding.left}, ${padding.top})`}>
              {/* Grid lines — horizontal */}
              {yLabels.map((_, i) => {
                const y = (i / (yLabels.length - 1)) * chartHeight
                return (
                  <line
                    key={`h-${i}`}
                    x1={0}
                    y1={y}
                    x2={chartWidth}
                    y2={y}
                    className="stroke-border-default"
                    strokeWidth={0.5}
                  />
                )
              })}
              {/* Grid lines — vertical */}
              {xLabels.map((_, i) => {
                const x = (i / (xLabels.length - 1)) * chartWidth
                return (
                  <line
                    key={`v-${i}`}
                    x1={x}
                    y1={0}
                    x2={x}
                    y2={chartHeight}
                    className="stroke-border-default"
                    strokeWidth={0.5}
                  />
                )
              })}

              {/* Y-axis labels */}
              {yLabels.map((label, i) => {
                const y = (i / (yLabels.length - 1)) * chartHeight
                return (
                  <text
                    key={`yl-${i}`}
                    x={-8}
                    y={y + 3}
                    textAnchor="end"
                    className="fill-fg-secondary"
                    fontSize={8}
                  >
                    {label}
                  </text>
                )
              })}

              {/* X-axis labels */}
              {xLabels.map((label, i) => {
                const x = (i / (xLabels.length - 1)) * chartWidth
                return (
                  <text
                    key={`xl-${i}`}
                    x={x}
                    y={chartHeight + 16}
                    textAnchor="middle"
                    className="fill-fg-secondary"
                    fontSize={8}
                  >
                    {label}
                  </text>
                )
              })}

              {/* Axis titles */}
              <text
                x={chartWidth / 2}
                y={chartHeight + 28}
                textAnchor="middle"
                className="fill-fg-secondary"
                fontSize={7}
              >
                Circulating asset market cap (% change, 30d)
              </text>
              <text
                x={-chartHeight / 2}
                y={-38}
                textAnchor="middle"
                className="fill-fg-secondary"
                fontSize={7}
                transform="rotate(-90)"
              >
                Asset transfer count (% change, 30d)
              </text>

              {/* Data points */}
              {dataPoints.map((point) => (
                <Dot
                  key={point.label}
                  point={point}
                  chartWidth={chartWidth}
                  chartHeight={chartHeight}
                />
              ))}

              {/* Callout for highlighted point */}
              <g className="opacity-0 group-hover/scatter:opacity-100 transition-opacity duration-300">
                <rect
                  x={chartWidth * 0.68}
                  y={chartHeight * 0.2}
                  width={145}
                  height={52}
                  rx={4}
                  className="fill-bg-surface"
                  opacity={0.9}
                />
                <text
                  x={chartWidth * 0.68 + 8}
                  y={chartHeight * 0.2 + 14}
                  className="fill-fg-default"
                  fontSize={9}
                  fontWeight={500}
                >
                  USDC inflows = a leading
                </text>
                <text
                  x={chartWidth * 0.68 + 8}
                  y={chartHeight * 0.2 + 24}
                  className="fill-fg-default"
                  fontSize={9}
                  fontWeight={500}
                >
                  indicator of continued growth
                </text>
                <text
                  x={chartWidth * 0.68 + 8}
                  y={chartHeight * 0.2 + 34}
                  className="fill-fg-default"
                  fontSize={9}
                  fontWeight={500}
                >
                  for Hyperliquid (~$200M
                </text>
                <text
                  x={chartWidth * 0.68 + 8}
                  y={chartHeight * 0.2 + 44}
                  className="fill-fg-default"
                  fontSize={9}
                  fontWeight={500}
                >
                  revenue YTD).
                </text>
              </g>

              {/* Label for highlighted point */}
              <text
                x={(88 / 100) * chartWidth}
                y={chartHeight - (48 / 100) * chartHeight - 14}
                textAnchor="middle"
                className="fill-fg-default"
                fontSize={7}
                fontWeight={500}
              >
                USDC (HyperEVM)
              </text>

              {/* Labels are now rendered inside each Dot component */}
            </g>
          </svg>

          {/* Branding */}
          <div className="absolute bottom-8 right-6 flex items-baseline gap-1">
            <span className="text-[10px] font-medium text-fg-secondary tracking-tight">
              token terminal
            </span>
            <span className="text-[10px] text-fg-secondary">Research</span>
          </div>
        </div>
      </div>
    </div>
  )
}
